import React, { useContext } from "react";
import type { DocumentLink } from "@/models/document-link/DocumentLink";
import { DocumentLinksLoadingContext } from "./DocumentLinksLoadingContext";
import { useReferencesSidebarFiltersStore } from "@/stores/references-sidebar-filters.store";
import { shouldShowDocumentLinkAnchor } from "@/utils/documentLinkOtherSide";
import type { WorkspaceReferenceVisibilityContext } from "@/utils/documentLinkWorkspaceVersionCollapse";
import { NEW_TAB_MODIFIER_TOOLTIP } from "@/lib/navigation/appNavigation";

interface ParsedDocumentLinkAnchorProps {
  allDocumentLinks?: DocumentLink[];
  workspaceVisibilityContext?: WorkspaceReferenceVisibilityContext;
  link?: DocumentLink;
  linkId?: string;
  anchorProps: React.ComponentPropsWithoutRef<"a"> & { [key: string]: unknown };
  children: React.ReactNode;
  navigateToDocumentLink: (
    linkId: string,
    event?: React.MouseEvent | MouseEvent
  ) => void;
  documentLinkHref?: string;
  /** The anchor carries an id that could resolve to a document link. */
  hasPotentialDocLink: boolean;
  currentDocumentId?: string;
}

/**
 * Native `<a id="doclink_*">` from legislation HTML. Subscribes to the references
 * sidebar store so "Vis historiske" toggles visibility without re-parsing HTML.
 */
export const ParsedDocumentLinkAnchor: React.FC<ParsedDocumentLinkAnchorProps> = ({
  allDocumentLinks,
  workspaceVisibilityContext,
  link,
  linkId,
  anchorProps,
  children,
  navigateToDocumentLink,
  documentLinkHref,
  hasPotentialDocLink,
  currentDocumentId,
}) => {
  const isLoadingDocumentLinks = useContext(DocumentLinksLoadingContext);
  const isStillLoading = hasPotentialDocLink && isLoadingDocumentLinks && !link;
  const showHistorical = useReferencesSidebarFiltersStore((s) => s.showHistorical);
  const showEarlierWorkspaceVersions = useReferencesSidebarFiltersStore(
    (s) => s.showEarlierWorkspaceVersions
  );

  if (
    link &&
    !shouldShowDocumentLinkAnchor(
      [link],
      currentDocumentId,
      showHistorical,
      showEarlierWorkspaceVersions,
      allDocumentLinks,
      workspaceVisibilityContext
    )
  ) {
    return <span className="doc-link-plain">{children}</span>;
  }

  const props = { ...anchorProps };

  if (link && linkId) {
    if (documentLinkHref) {
      props.href = documentLinkHref;
    }
    props.title = NEW_TAB_MODIFIER_TOOLTIP;
    props.onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (e.button !== 0) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      navigateToDocumentLink(linkId, e);
    };
    props.onAuxClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (e.button === 1) {
        e.preventDefault();
        e.stopPropagation();
        navigateToDocumentLink(linkId, e);
      }
    };
    const existingStyle = (props.style as React.CSSProperties) || {};
    props.style = {
      ...existingStyle,
      cursor: "pointer",
    };
  } else if (isStillLoading) {
    props.onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const existingStyle = (props.style as React.CSSProperties) || {};
    props.style = {
      ...existingStyle,
      cursor: "wait",
      opacity: 0.7,
    };
  }

  return <a {...props}>{children}</a>;
};
